import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Home, Plus, Inbox, CreditCard, Settings, BarChart3, List, Boxes, ArrowLeft, ChevronRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import NotificationBell from "./NotificationBell";
import { EmailSettingsButton } from "./EmailSettingsButton";
import UserMenu from "./UserMenu";

/**
 * AppShell — shared frame for every authed route.
 *
 * Visual language (Petpooja-inspired, approved 22 Jun 2026):
 *   • Inter sans across the board (set globally in tailwind.config.js + index.css)
 *   • Olive #6B7F3F as the single accent color
 *   • White cards on #F7F8FA background
 *   • Subtle slate-200 borders, rounded-2xl, gentle shadows
 *   • Generous whitespace
 *
 * All data-testids and functionality preserved from the previous version.
 */
export const AppShell = ({ children, title, back, right }) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-[#F7F8FA] text-slate-900 antialiased pb-28 lg:pb-10">
      <header
        data-testid="app-header"
        className="sticky top-0 z-30 bg-white border-b border-slate-200"
      >
        <div className="max-w-7xl mx-auto px-4 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4 min-w-0">
            {back ? (
              <button
                data-testid="header-back-btn"
                onClick={() => navigate(back)}
                className="rounded-full px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 inline-flex items-center gap-1.5 transition-colors"
              >
                <ArrowLeft size={14} strokeWidth={2}/> Back
              </button>
            ) : (
              <div className="flex items-baseline gap-1.5">
                <span className="font-bold text-xl tracking-tight">VOW</span>
                <span className="text-[11px] font-medium tracking-wide text-olive">Control</span>
              </div>
            )}
            {title && (
              <div className="hidden sm:flex items-center gap-1.5 text-sm">
                <span className="text-slate-400">Workspace</span>
                <ChevronRight size={14} className="text-slate-300" strokeWidth={2}/>
                <span className="font-medium text-slate-900 capitalize">{title.toLowerCase()}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {right}
            {user && (
              <div className="flex items-center gap-2" data-testid="header-user">
                <NotificationBell />
                <EmailSettingsButton />
                <div className="text-right hidden sm:block">
                  <div className="text-[11px] text-slate-500 capitalize">{user.role}</div>
                  <div className="text-sm font-semibold leading-tight max-w-[140px] truncate">{user.name || user.email}</div>
                </div>
                <UserMenu />
              </div>
            )}
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto">{children}</main>
      <BottomNav />
    </div>
  );
};

const BottomNav = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const loc = useLocation();
  if (!user) return null;

  const role = user.role;
  const verticals = user.verticals || ["finance"];
  const isAdmin = role === "admin";
  const hasFinance = isAdmin || verticals.includes("finance");
  const hasInventory = isAdmin || verticals.includes("inventory");
  // v2.11 — permission-driven nav
  const perms = user.permissions_effective || {};
  const canCreate = isAdmin || !!perms.create_requests;
  const canQueue  = isAdmin || !!perms.view_payment_queue;
  const canReport = isAdmin || !!perms.view_reports;

  const homeTo = (!hasFinance && hasInventory) ? "/inventory" : "/dashboard";
  const items = [];
  items.push({ to: homeTo, icon: Home, label: "Home", testid: "nav-home" });

  if (hasFinance) {
    if (canCreate) {
      items.push({ to: "/new-request", icon: Plus, label: "New", testid: "nav-new", primary: !isAdmin && role === "requestor" });
      items.push({ to: "/my-requests", icon: List, label: "Mine", testid: "nav-mine" });
    }
    if (canQueue) {
      items.push({ to: "/approvals", icon: Inbox, label: "Approve", testid: "nav-approve", primary: role === "approver" });
      items.push({ to: "/queue", icon: CreditCard, label: "Queue", testid: "nav-queue", primary: role === "accounts" });
    }
    if (canReport) {
      items.push({ to: "/reports", icon: BarChart3, label: "Reports", testid: "nav-reports" });
    }
  }
  if (hasInventory) {
    items.push({ to: "/inventory", icon: Boxes, label: "Stock", testid: "nav-inventory" });
  }
  if (isAdmin) {
    items.push({ to: "/admin", icon: Settings, label: "Admin", testid: "nav-admin" });
  }

  return (
    <nav
      data-testid="bottom-nav"
      className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md lg:hidden bg-white border-t border-slate-200 flex items-stretch z-50 shadow-[0_-1px_4px_rgba(15,23,42,0.04)]"
    >
      {items.map((it) => {
        const Icon = it.icon;
        const active = loc.pathname === it.to;
        return (
          <button
            key={it.to + "-" + it.label}
            data-testid={it.testid}
            onClick={() => navigate(it.to)}
            className={`flex-1 flex flex-col items-center justify-center py-3 gap-1 transition-colors ${
              active ? "text-olive" : "text-slate-500 hover:text-slate-900"
            }`}
          >
            <Icon size={18} strokeWidth={1.8} />
            <span className="text-[10px] font-medium">{it.label}</span>
          </button>
        );
      })}
    </nav>
  );
};
